import React, { useState, useRef, useEffect } from 'react';
import { LuSettings2 } from 'react-icons/lu';
import './MapConfigPanel.css';

interface MapConfigPanelProps {
  showHeatmap: boolean;
  onHeatmapChange: (value: boolean) => void;
  neighbourhoodAnonymous: boolean;
  onNeighbourhoodAnonymousChange: (value: boolean) => void;
  hideClustering: boolean;
  onHideClusteringChange: (value: boolean) => void;
  markerSize: number;
  onMarkerSizeChange: (value: number) => void;
}

const MapConfigPanel: React.FC<MapConfigPanelProps> = ({
  showHeatmap,
  onHeatmapChange,
  neighbourhoodAnonymous,
  onNeighbourhoodAnonymousChange,
  hideClustering,
  onHideClusteringChange,
  markerSize,
  onMarkerSizeChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Local state for pending changes
  const [localHeatmap, setLocalHeatmap] = useState(showHeatmap);
  const [localAnonymous, setLocalAnonymous] = useState(neighbourhoodAnonymous);
  const [localHideClustering, setLocalHideClustering] = useState(hideClustering);
  const [localMarkerSize, setLocalMarkerSize] = useState(markerSize);

  // Sync local state when props change (e.g. from clear all filters)
  useEffect(() => {
    setLocalHeatmap(showHeatmap);
  }, [showHeatmap]);

  useEffect(() => {
    setLocalAnonymous(neighbourhoodAnonymous);
  }, [neighbourhoodAnonymous]);

  useEffect(() => {
    setLocalHideClustering(hideClustering);
  }, [hideClustering]);

  useEffect(() => {
    setLocalMarkerSize(markerSize);
  }, [markerSize]);

  // Close panel when clicking/tapping outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  const handleSave = () => {
    onHeatmapChange(localHeatmap);
    onNeighbourhoodAnonymousChange(localAnonymous);
    onHideClusteringChange(localHideClustering);
    onMarkerSizeChange(localMarkerSize);
    setIsOpen(false);
  };

  const hasChanges = localHeatmap !== showHeatmap || localAnonymous !== neighbourhoodAnonymous || localHideClustering !== hideClustering || localMarkerSize !== markerSize;

  return (
    <div className="map-config-panel" ref={panelRef}>
      <button
        className={`map-config-toggle ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Map Settings"
      >
        <LuSettings2 size={18} />
      </button>

      {isOpen && (
        <div className="map-config-dropdown">
          <div className="map-config-header">
            <span className="map-config-title">Map Settings</span>
          </div>
          <div className="map-config-body">
            <label className="map-config-option">
              <input
                type="checkbox"
                checked={localHeatmap}
                onChange={(e) => setLocalHeatmap(e.target.checked)}
              />
              <span className="map-config-option-label">Crime Heatmap</span>
            </label>
            <label className="map-config-option">
              <input
                type="checkbox"
                checked={localAnonymous}
                onChange={(e) => setLocalAnonymous(e.target.checked)}
              />
              <span className="map-config-option-label">Show Neighbours as Anonymous</span>
            </label>
            <label className="map-config-option">
              <input
                type="checkbox"
                checked={localHideClustering}
                onChange={(e) => setLocalHideClustering(e.target.checked)}
              />
              <span className="map-config-option-label">Hide Clusters</span>
            </label>
            <div className="map-config-slider-group">
              <div className="map-config-slider-header">
                <span className="map-config-option-label">Marker Size</span>
                <span className="map-config-slider-value">{localMarkerSize}%</span>
              </div>
              <input
                type="range"
                className="map-config-slider"
                min={50}
                max={100}
                step={5}
                value={localMarkerSize}
                onChange={(e) => setLocalMarkerSize(Number(e.target.value))}
              />
              <div className="map-config-slider-labels">
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>
          </div>
          <div className="map-config-footer">
            <button
              className={`map-config-save-btn ${!hasChanges ? 'disabled' : ''}`}
              onClick={handleSave}
              disabled={!hasChanges}
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapConfigPanel;
